/**
 * TakeAssessment — /assess/take/:scaleId (Phase 3, mock).
 *
 * Administers the chosen scale by rendering its SurveyDefinition through the
 * EXISTING builder engine (SurveyRunner → scoreSurvey), so reverse-scoring,
 * progress, validation (all items required), the score panel and the licence
 * line are all reused, not reimplemented. On scoring, the result is recorded in
 * AssessFlowContext and a "Continue to conversation" button advances the flow.
 *
 * The measurement point (Baseline / Interim / Endpoint) is NOT sent from here. The server derives
 * it from the session the chooser picked — first session is the baseline, last is the endpoint.
 * This file used to hardcode "Baseline" on every capture, which filed end-of-programme runs as
 * start-of-programme ones and left those participants unpaired in the outcome report.
 *
 * TODO[RBAC]: gate at the route level with the AssessView permit once defined.
 */
import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Typography } from "@mui/material";
import { ArrowForwardOutlined } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useMobile } from "../../../hooks/useMobile";
import { SurveyRunner } from "../builder/SurveyRunner";
import { captureSurveyResponse, getSurveyTemplate } from "../../../client/api/survey";
import { allQuestions, SurveyDefinition } from "../builder/survey.types";
import { questionScore } from "../builder/scoring";
import { toDefinition } from "../builder/builder.apiPersistence";
import Spinner from "../../layout/Spinner";
import { findScale } from "./scaleCatalog";
import { AssessFlowStepper } from "./AssessFlowStepper";
import { useAssessFlow } from "./AssessFlowContext";

export const TakeAssessment = (): JSX.Element => {
    const { isMobile, isTablet } = useMobile();
    const padding = !isTablet && isMobile ? 2 : 4;
    const navigate = useNavigate();
    const { scaleId } = useParams<{ scaleId: string }>();
    const { state, recordScore, recordPersisted, chooseTemplate } = useAssessFlow();
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    /**
     * The route param is either a persisted template id (all digits, the real path) or a
     * catalog key from the demo scale list. Server ids are loaded and administered for real;
     * catalog entries remain a local preview. Mirrors `isServerId` in builder.apiPersistence.
     */
    const isServerTemplate = /^\d+$/.test(scaleId ?? "");
    const [loaded, setLoaded] = useState<SurveyDefinition | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loading, setLoading] = useState(isServerTemplate);

    useEffect(() => {
        if (!isServerTemplate || !scaleId) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        getSurveyTemplate(Number(scaleId))
            .then((template) => {
                if (cancelled) return;
                setLoaded(toDefinition(template));
                // versionId is deliberately omitted: PersistedSurveyTemplate exposes
                // `currentVersion` (an ordinal), not the SurveyTemplateVersion row id the capture
                // endpoint expects. Sending the ordinal would bind answers to the wrong snapshot
                // — or 409 — so leave it null until the API surfaces the row id.
                chooseTemplate({ id: template.id });
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setLoadError(error instanceof Error ? error.message : "That survey could not be loaded.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [scaleId, isServerTemplate, chooseTemplate]);

    const entry = findScale(scaleId);

    /**
     * The flow's chosen template is the instrument being administered ONLY when this route is that
     * same server template. A catalog preview never calls chooseTemplate, so after taking a real
     * survey and then opening a preview, `state.template` still holds the previous one — capturing
     * against it would file these answers under the wrong instrument and carry them into that
     * scale's reports, where nothing downstream could tell they came from a different survey.
     * The banner and the capture read the same predicate so what the practitioner is promised and
     * what happens cannot diverge.
     */
    const willPersist =
        isServerTemplate &&
        state.template != null &&
        String(state.template.id) === scaleId &&
        // A session is required (PR #5 review): it is what the server reads the measurement point
        // from. Checking it HERE rather than at submit time is the point — the banner then tells
        // the practitioner the run will not be saved before they answer 14 items, instead of a 400
        // afterwards.
        state.subject?.programmeId != null &&
        state.subject?.sessionId != null;

    if (loading) {
        return (
            <Box p={padding} width={1}>
                <Spinner />
            </Box>
        );
    }

    if (loadError) {
        return (
            <Box p={padding} width={1}>
                {!isTablet && isMobile && <Box pt={8} />}
                <Alert severity="error">
                    {loadError}{" "}
                    <Button variant="text" onClick={() => navigate("/assess/new")}>
                        Choose another survey
                    </Button>
                </Alert>
            </Box>
        );
    }

    if (!loaded && !entry) {
        return (
            <Box p={padding} width={1}>
                {!isTablet && isMobile && <Box pt={8} />}
                <Alert severity="warning">
                    That scale could not be found.{" "}
                    <Button variant="text" onClick={() => navigate("/assess/new")}>
                        Choose a scale
                    </Button>
                </Alert>
            </Box>
        );
    }

    // A loaded server template wins; the catalog entry is the preview fallback.
    const survey = loaded ?? (entry as NonNullable<typeof entry>).survey;
    const title = loaded ? loaded.title : (entry as NonNullable<typeof entry>).name;
    const scaleKey = loaded ? loaded.id : (entry as NonNullable<typeof entry>).id;

    return (
        <Box p={padding} width={1}>
            {!isTablet && isMobile && <Box pt={8} />}

            <Typography variant="h2" color="textPrimary" mb={3}>
                {title}
            </Typography>

            <AssessFlowStepper activeStep={1} />

            <Card>
                <CardContent>
                    <SurveyRunner
                        survey={survey}
                        previewBanner={
                            <Alert severity={willPersist ? "info" : "warning"} sx={{ mb: 3 }}>
                                {willPersist
                                    ? "Answer every item to score and save this assessment."
                                    : "Preview only — this run is not linked to a participant and session, so the score will not be saved."}
                            </Alert>
                        }
                        onExit={() => navigate("/assess")}
                        submitting={saving}
                        persisted={state.persistedResponseId != null}
                        onScored={async (result, values) => {
                            if (result.finalScore === null || result.rawScore === null) {
                                return;
                            }
                            recordScore({
                                scaleId: scaleKey,
                                scaleName: title,
                                rawScore: result.rawScore,
                                finalScore: result.finalScore,
                                rawRange: survey.scoring.rawRange,
                                multiplier: survey.scoring.multiplier,
                                higherIsBetter: survey.higherIsBetter,
                                answeredOf: { answered: result.answered, total: result.total },
                                cutoffLabel: result.cutoffLabel,
                                capturedAt: new Date().toISOString(),
                            });

                            // Persist only when the flow carries the template THIS route administers.
                            // Until the chooser collects one the score stays local — better an honest
                            // "not saved" banner than a 4xx the practitioner cannot act on, and far
                            // better than a save that silently attaches to the wrong instrument.
                            const { template, subject } = state;
                            if (!template || !willPersist || subject?.sessionId == null) {
                                return;
                            }

                            setSaving(true);
                            setSaveError(null);
                            try {
                                // No `administration`: the server infers Baseline / Interim /
                                // Endpoint from the session. It used to be hardcoded "Baseline"
                                // here, so a session-6 run was stored as a start-of-programme
                                // measurement and the participant never paired in the report.
                                const { id } = await captureSurveyResponse({
                                    templateId: template.id,
                                    templateVersionId: template.versionId ?? null,
                                    participantId: subject.participantId,
                                    programmeId: subject.programmeId,
                                    sessionId: subject.sessionId,
                                    rawScore: result.rawScore,
                                    finalScore: result.finalScore,
                                    items: allQuestions(survey)
                                        .filter((q) => q.scored && values[q.id] !== undefined)
                                        .map((q) => ({
                                            questionKey: q.id,
                                            value: Number(values[q.id]),
                                            adjustedValue: questionScore(survey, q, values[q.id]),
                                        })),
                                });
                                recordPersisted(id);
                            } catch (error) {
                                // The endpoint rejects cross-tenant ids (404) and integrity breaches
                                // such as a version/template mismatch or an unenrolled participant
                                // (400/409). Surface the reason — a generic failure leaves the
                                // practitioner with no idea whether to retry or fix the selection.
                                setSaveError(
                                    error instanceof Error
                                        ? error.message
                                        : "The assessment was scored but could not be saved.",
                                );
                            } finally {
                                setSaving(false);
                            }
                        }}
                        scoreFooter={
                            <Box>
                                {saveError ? (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {saveError} The score above is correct — it has not been saved.
                                    </Alert>
                                ) : null}
                                {state.persistedResponseId ? (
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        Assessment saved.
                                    </Alert>
                                ) : null}
                                {/*
                                 * A bound run that did not persist must not walk forward. The error
                                 * alert says the score was not saved, but a practitioner who
                                 * advances anyway carries a number the record does not hold into
                                 * the conversation and the report. A preview (nothing to persist)
                                 * is free to continue — there is no record to be missing from.
                                 */}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    endIcon={<ArrowForwardOutlined />}
                                    disabled={saving || (willPersist && state.persistedResponseId == null)}
                                    onClick={() => navigate("/assess/conversation")}
                                >
                                    {saving ? "Saving…" : "Continue to conversation"}
                                </Button>
                            </Box>
                        }
                    />
                </CardContent>
            </Card>
        </Box>
    );
};

export default TakeAssessment;
